/**
 * DailyBriefFilter
 *
 * Filter dropdown component for the Daily Brief. Allows users to filter athletes by group.
 * Currently supports single-select group filtering with "All Athletes" as the default option.
 *
 * Features:
 * - Searchable dropdown with custom styling
 * - Auto-saves filter selection to user settings
 * - "Show All" button when filtered
 *
 * @component
 */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import Select, { components } from 'react-select';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  setFilterType,
  setFilterIds,
  fetchFilterSelections,
  updateSettings,
} from '../ducks/dailyBriefActions';

const FilterContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  width: 100%;
  justify-content: space-between;
`;

const FilterLabel = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: #9e9e9e;
  white-space: nowrap;
`;

const FilterDropdown = styled('div')`
  min-width: 150px;
  max-width: calc(100vw - 100px);
  position: relative;
  flex-shrink: 1;

  @media (min-width: 768px) {
    min-width: 220px;
    max-width: none;
  }

  &.menu-open {
    min-width: 280px;
  }
`;

const GradientBorder = styled('div')`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #267deb 0%, #5d45f9 100%);
  border-radius: 6px;
  z-index: 0;
  pointer-events: none;
`;

const LeftSection = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const ShowAllButton = styled('button')`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(48, 49, 51, 0.1),
    0px 0px 1px rgba(48, 49, 51, 0.05);
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: #d9d9d9;
  }
`;

const getCustomSelectStyles = (isSmallScreen) => ({
  control: (provided) => ({
    ...provided,
    minHeight: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '4px',
    boxShadow:
      '0px 1px 3px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    backgroundColor: '#ffffff',
    position: 'relative',
    zIndex: 1,
    width: '100%',
    '&:hover': {
      borderColor: 'transparent',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 16px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#f0f0f0',
    borderRadius: '3px',
    margin: '2px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#444444',
    fontSize: '13px',
    padding: '2px 6px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#666666',
    ':hover': {
      backgroundColor: '#ff6600',
      color: 'white',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    border: '1px solid #EEEEEE',
    boxShadow:
      '0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    borderRadius: '4px',
    overflow: 'hidden',
    ...(isSmallScreen
      ? { width: '100%', minWidth: 0 }
      : { width: 'auto', minWidth: '100%' }),
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '288px',
    overflowY: 'auto',
    overflowX: 'hidden',
  }),
  option: (provided, state) => {
    let backgroundColor = '#FFFFFF';
    if (state.isSelected) {
      backgroundColor = '#FCFCFC';
    } else if (state.isFocused) {
      backgroundColor = '#f5f5f5';
    }

    return {
      ...provided,
      backgroundColor,
      color: '#444444',
      cursor: 'pointer',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    };
  },
  placeholder: (provided) => ({
    ...provided,
    margin: 0,
    color: '#9E9E9E',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  }),
  clearIndicator: () => ({
    display: 'none',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#9E9E9E',
    '&:hover': {
      color: '#9E9E9E',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: '#444444',
    margin: 0,
    padding: 0,
    '& input': {
      font: 'inherit',
    },
    '& input::placeholder': {
      color: '#9E9E9E',
      opacity: 1,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    whiteSpace: 'nowrap',
    position: 'static',
    transform: 'none',
    marginRight: '4px',
  }),
});

const PlaceholderWithIcon = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  height: 100%;
  justify-content: flex-start;
  color: #444444;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

// Custom SingleValue component to show icon with selected value
const CustomSingleValue = ({ children, ...props }) => (
  <components.SingleValue {...props}>
    <PlaceholderWithIcon>
      <IcomoonReact iconSet={iconSet} icon='Users' size={20} color='#9E9E9E' />
      {children}
    </PlaceholderWithIcon>
  </components.SingleValue>
);

CustomSingleValue.propTypes = {
  children: PropTypes.node,
};
CustomSingleValue.defaultProps = {
  children: null,
};

const DailyBriefFilter = () => {
  const dispatch = useDispatch();
  const selectRef = useRef(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const filterIds = useSelector((state) => state.dailyBrief.ui.filterIds);
  const filterSelections = useSelector(
    (state) => state.dailyBrief.data.filterSelections,
  );
  const isLoadingSelections = useSelector(
    (state) => state.dailyBrief.data.isLoadingSelections,
  );
  const settings = useSelector((state) => state.dailyBrief.data.settings);

  const accountCode = currentUser?.accountCode;

  useEffect(() => {
    // Always fetch group selections (filterType 2)
    if (accountCode) {
      dispatch(fetchFilterSelections(accountCode, 2));
    }
  }, [accountCode, dispatch]);

  const handleFilterSelectionChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : null;

    // Handle "All Athletes" option or clear action
    if (value === 'all' || value === null) {
      dispatch(setFilterType(null));
      dispatch(setFilterIds(null));

      // Omit filter fields entirely for "All Athletes" - backend doesn't accept null
      const newSettings = {};

      // Only include sort fields if they have values
      if (settings?.defaultSortBy) {
        newSettings.defaultSortBy = settings.defaultSortBy;
      }
      if (settings?.defaultSortOrder) {
        newSettings.defaultSortOrder = settings.defaultSortOrder;
      }

      dispatch(updateSettings(accountCode, newSettings));
      return;
    }

    const newFilterType = 2;
    dispatch(setFilterType(newFilterType));
    dispatch(setFilterIds(value));

    const newSettings = {
      defaultFilterType: Number(newFilterType),
      defaultFilterIds: Array.isArray(value)
        ? value.map(Number)
        : Number(value),
    };

    if (settings?.defaultSortBy) {
      newSettings.defaultSortBy = settings.defaultSortBy;
    }
    if (settings?.defaultSortOrder) {
      newSettings.defaultSortOrder = settings.defaultSortOrder;
    }

    dispatch(updateSettings(accountCode, newSettings));
  };

  // Format options for the single-select dropdown
  const getSelectionOptions = () => {
    const options = [];

    // Add "All Athletes" option at the top
    options.push({
      value: 'all',
      label: 'All Athletes',
    });

    // Add groups if available
    if (filterSelections && filterSelections.length > 0) {
      filterSelections.forEach((item) => {
        options.push({
          value: item.id,
          label: item.name,
        });
      });
    }

    return options;
  };

  const selectedValue = getSelectionOptions().find((opt) => {
    // Handle "All Athletes" selection when filterIds is null
    if (filterIds === null && opt.value === 'all') {
      return true;
    }
    // Handle group selection
    return opt.value === filterIds;
  });

  // Prevent jumping by showing loading state until data is ready
  const isDataReady = !isLoadingSelections && Array.isArray(filterSelections);

  // Custom Option component to show checkmarks
  const CustomOption = (props) => {
    const {
      children, isSelected, innerRef, innerProps,
    } = props;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: isSelected ? '#FCFCFC' : '#FFFFFF',
          color: isSelected ? '#BDBDBD' : '#444444',
          cursor: 'pointer',
          fontFamily: 'Nunito Sans',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '150%',
          width: '100%',
          boxSizing: 'border-box',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isSelected
            ? '#FCFCFC'
            : '#FFFFFF';
        }}
      >
        <span
          style={{
            flex: '1',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {children}
        </span>
        {isSelected && (
          <IcomoonReact
            iconSet={iconSet}
            icon='checkmark'
            size={16}
            color='#9E9E9E'
            style={{
              flexShrink: 0,
              marginLeft: '8px',
            }}
          />
        )}
      </div>
    );
  };

  CustomOption.propTypes = {
    children: PropTypes.node,
    isSelected: PropTypes.bool,
    innerRef: PropTypes.func,
    innerProps: PropTypes.shape({}),
  };
  CustomOption.defaultProps = {
    children: null,
    isSelected: false,
    innerRef: undefined,
    innerProps: undefined,
  };

  // Custom ValueContainer component for react-select
  const CustomValueContainer = (props) => {
    const { children, selectProps, ...restProps } = props;
    const showOverlay = menuIsOpen && !selectProps.inputValue && isDataReady;
    return (
      <components.ValueContainer {...restProps}>
        {showOverlay && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '16px',
              color: '#9E9E9E',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <IcomoonReact
              iconSet={iconSet}
              icon='Users'
              size={20}
              color='#9E9E9E'
            />
            <span
              style={{
                marginLeft: '8px',
                fontWeight: 400,
              }}
            >
              Search for a group
            </span>
          </div>
        )}
        {children}
      </components.ValueContainer>
    );
  };

  CustomValueContainer.propTypes = {
    children: PropTypes.node,
    selectProps: PropTypes.shape({
      inputValue: PropTypes.string,
    }),
  };
  CustomValueContainer.defaultProps = {
    children: null,
    selectProps: {},
  };

  // Check if a group is selected (not "All Athletes")
  const hasGroupSelected = filterIds !== null && filterIds !== 'all';

  const handleShowAll = () => {
    dispatch(setFilterType(null));
    dispatch(setFilterIds(null));

    // Omit filter fields entirely for "All Athletes" - backend doesn't accept null
    const newSettings = {};

    // Only include sort fields if they have values
    if (settings?.defaultSortBy) {
      newSettings.defaultSortBy = settings.defaultSortBy;
    }
    if (settings?.defaultSortOrder) {
      newSettings.defaultSortOrder = settings.defaultSortOrder;
    }

    dispatch(updateSettings(accountCode, newSettings));
  };

  return (
    <FilterContainer>
      <LeftSection>
        <FilterLabel>Viewing</FilterLabel>
        <FilterDropdown className={menuIsOpen ? 'menu-open' : ''}>
          <GradientBorder />
          <Select
            ref={selectRef}
            styles={getCustomSelectStyles(isSmallScreen)}
            value={selectedValue || null}
            onChange={handleFilterSelectionChange}
            options={getSelectionOptions()}
            isLoading={isLoadingSelections}
            placeholder='All Athletes'
            hideSelectedOptions={false}
            isSearchable
            onMenuOpen={() => {
              setMenuIsOpen(true);
            }}
            onMenuClose={() => {
              setMenuIsOpen(false);
            }}
            components={{
              Placeholder: (props) => {
                // Don't show placeholder until data is ready
                if (!isDataReady) return null;

                const text = menuIsOpen ? 'Search for a group' : 'All Athletes';
                return (
                  <components.Placeholder {...props}>
                    <PlaceholderWithIcon>
                      <IcomoonReact
                        iconSet={iconSet}
                        icon='Users'
                        size={20}
                        color='#9E9E9E'
                      />
                      <span
                        style={{
                          fontWeight: 700,
                        }}
                      >
                        {text}
                      </span>
                    </PlaceholderWithIcon>
                  </components.Placeholder>
                );
              },
              ValueContainer: CustomValueContainer,
              Option: CustomOption,
              SingleValue: (props) => {
                if (menuIsOpen) return null;
                return <CustomSingleValue {...props} />;
              },
            }}
            classNamePrefix='react-select'
            isClearable
            filterOption={(option, searchText) => {
              if (!searchText) return true;
              return option.label
                .toLowerCase()
                .includes(searchText.toLowerCase());
            }}
            noOptionsMessage={() => 'No groups found'}
          />
        </FilterDropdown>
        {hasGroupSelected && (
          <ShowAllButton onClick={handleShowAll}>Show All</ShowAllButton>
        )}
      </LeftSection>
    </FilterContainer>
  );
};

export default DailyBriefFilter;
